/**
 * Agent Store — single source of truth for user's agent data.
 *
 * Mirrors skillStore.ts pattern. Both agent pages and task UX read from here.
 * Socket events and CRUD operations call fetchMyAgents() to refresh.
 *
 * Backend: GET /api/items/agent/mine (canonical Items endpoint).
 * The legacy /api/agents/summaries + /api/agents/installed routes were removed
 * in PR-1 of the abilities cleanup; this store maps the canonical Item shape
 * down to the legacy AgentSummary shape so downstream consumers (AgentsPage,
 * task layout, subagent handlers) don't need to change.
 *
 * CRUD endpoints (POST/PUT/DELETE/GET /api/agents[/:slug]) are still served by
 * agents-routes.handler.ts and unchanged here.
 */

import { create } from "zustand";
import { devtools } from "zustand/middleware";
import type { AgentSummary } from "@bash/shared";
import { ANALYTICS_EVENTS } from "@bash/shared";
import { resilientFetch } from "@/lib/fetch";
import { posthog } from "@/lib/posthog";

const SERVER_URL =
  process.env.NEXT_PUBLIC_SERVER_URL || "http://localhost:3005";

export interface CreateAgentInput {
  slug: string;
  name: string;
  description: string;
  prompt: string;
  tools?: string[];
  model?: "haiku" | "sonnet" | "opus" | "inherit";
  skills?: string[];
  mcps?: string[];
}

export interface UpdateAgentInput {
  name?: string;
  description?: string;
  prompt?: string;
  tools?: string[];
  model?: "haiku" | "sonnet" | "opus" | "inherit";
  skills?: string[];
  mcps?: string[];
}

export interface AgentFilterParams {
  search?: string;
  creator?: string;
  category?: string;
}

interface AgentStore {
  agents: AgentSummary[];
  loading: boolean;
  error: string | null;
  hasMore: boolean;
  total: number;

  // Internal
  _authToken: string | null;
  _hasFetched: boolean;
  _requestGen: number;
  _offset: number;
  _lastFilters: AgentFilterParams | undefined;
  // Full filtered set, kept client-side so loadMore() can slice without a refetch.
  _allFiltered: AgentSummary[];

  // Actions
  setAuthToken: (token: string) => void;
  fetchMyAgents: (
    params?: AgentFilterParams & { reset?: boolean }
  ) => Promise<void>;
  loadMore: () => void;
  invalidate: () => void;

  // CRUD
  createAgent: (input: CreateAgentInput) => Promise<boolean>;
  updateAgent: (slug: string, input: UpdateAgentInput) => Promise<boolean>;
  deleteAgent: (slug: string) => Promise<boolean>;
  getAgentDetail: (slug: string) => Promise<Record<string, unknown> | null>;
}

const DEFAULT_LIMIT = 30;

// ---------------------------------------------------------------------------
// Item → AgentSummary projection
// ---------------------------------------------------------------------------
interface ItemResponseRow {
  id: string;
  slug: string;
  scope: "system" | "marketplace" | "user";
  metadata?: {
    name?: string;
    description?: string;
    category?: string;
    icon?: string;
    color?: string;
    tools?: string[];
    mcps?: string[];
  } | null;
}

function itemToAgentSummary(item: ItemResponseRow): AgentSummary {
  const meta = item.metadata ?? {};
  return {
    slug: item.slug,
    name: meta.name ?? item.slug,
    description: meta.description ?? "",
    category: meta.category,
    icon: meta.icon,
    color: meta.color,
    mcps: meta.mcps,
    // `skills` isn't carried in canonical Item metadata — consumers needing it
    // call getAgentDetail() (GET /api/agents/:slug) for the detail view.
    skills: undefined,
    // `isSystem` is signalled by the canonical scope.
    isSystem: item.scope === "system",
  };
}

function matchesFilters(a: AgentSummary, f: AgentFilterParams): boolean {
  if (f.search) {
    const q = f.search.toLowerCase();
    const hit =
      a.name.toLowerCase().includes(q) ||
      a.description.toLowerCase().includes(q) ||
      a.slug.toLowerCase().includes(q);
    if (!hit) return false;
  }
  if (f.category && a.category !== f.category) return false;
  // `creator` filter on the dead /summaries endpoint distinguished system vs
  // user-authored. Mirror that here via the scope-derived isSystem flag.
  if (f.creator) {
    if (f.creator === "system" && !a.isSystem) return false;
    if (f.creator === "user" && a.isSystem) return false;
  }
  return true;
}

export const useAgentStore = create<AgentStore>()(
  devtools(
    (set, get) => ({
      agents: [],
      loading: false,
      error: null,
      hasMore: false,
      total: 0,
      _authToken: null,
      _hasFetched: false,
      _requestGen: 0,
      _offset: 0,
      _lastFilters: undefined,
      _allFiltered: [],

      setAuthToken: (token: string) => {
        const current = get()._authToken;
        if (current !== token) {
          set({ _authToken: token });
        }
      },

      fetchMyAgents: async (
        params?: AgentFilterParams & { reset?: boolean }
      ) => {
        const { _authToken } = get();
        if (!_authToken) return;

        // Default to reset=true (replace agents). reset=false = append (load more).
        const reset = params?.reset !== false;

        // Generation counter for stale request prevention
        const gen = get()._requestGen + 1;
        set({ _requestGen: gen });

        // Strip `reset` before building the filter set
        const { reset: _r, ...filterParams } = params || {};

        // Save filters for loadMore to reuse
        if (reset) {
          set({
            _lastFilters:
              Object.keys(filterParams).length > 0 ? filterParams : undefined,
          });
        }

        // Only show loading spinner on first fetch
        if (!get()._hasFetched) {
          set({ loading: true });
        }

        try {
          const headers: Record<string, string> = {
            "Content-Type": "application/json",
            Authorization: `Bearer ${_authToken}`,
          };

          // Canonical Items API — returns the user-scope set in one shot.
          // TODO(pagination): add server-side pagination if user agent counts grow.
          const url = `${SERVER_URL}/api/items/agent/mine`;

          const response = await resilientFetch(url, { headers });

          // Stale response — a newer request was fired while this was in-flight
          if (gen !== get()._requestGen) return;

          if (!response.ok) {
            throw new Error(`Failed to fetch agents: ${response.statusText}`);
          }

          const data = await response.json();
          const rawItems: ItemResponseRow[] = data.items || [];
          const allAgents = rawItems.map(itemToAgentSummary);

          // Apply filters client-side, then slice.
          const activeFilters = reset ? filterParams : get()._lastFilters || {};
          const filtered = allAgents.filter((a) =>
            matchesFilters(a, activeFilters)
          );

          const offset = reset ? 0 : get()._offset;
          const page = filtered.slice(0, offset + DEFAULT_LIMIT);
          const hasMore = filtered.length > page.length;

          if (gen === get()._requestGen) {
            set({
              agents: page,
              hasMore,
              total: filtered.length,
              _offset: page.length,
              _allFiltered: filtered,
              error: null,
              _hasFetched: true,
              loading: false,
            });
          }
        } catch (err) {
          if (gen === get()._requestGen) {
            set({
              error:
                err instanceof Error ? err.message : "Failed to fetch agents",
              loading: false,
            });
          }
        }
      },

      loadMore: () => {
        const { hasMore, loading, _hasFetched, _allFiltered, _offset } = get();
        if (!hasMore || loading || !_hasFetched) return;
        const page = _allFiltered.slice(0, _offset + DEFAULT_LIMIT);
        set({
          agents: page,
          _offset: page.length,
          hasMore: _allFiltered.length > page.length,
        });
      },

      invalidate: () => {
        set({ _hasFetched: false });
      },

      createAgent: async (input: CreateAgentInput) => {
        const { _authToken } = get();
        if (!_authToken) return false;

        try {
          const response = await resilientFetch(`${SERVER_URL}/api/agents`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${_authToken}`,
            },
            body: JSON.stringify(input),
          });

          if (!response.ok) {
            const data = await response.json().catch(() => ({}));
            throw new Error(
              data.error || `Failed to create agent: ${response.statusText}`
            );
          }

          posthog.capture(ANALYTICS_EVENTS.AGENT_CREATED, {
            has_custom_prompt: Boolean(input.prompt),
            skill_count: input.skills?.length ?? 0,
          });

          // Refresh agent list
          await get().fetchMyAgents();
          return true;
        } catch (err) {
          set({
            error:
              err instanceof Error ? err.message : "Failed to create agent",
          });
          return false;
        }
      },

      updateAgent: async (slug: string, input: UpdateAgentInput) => {
        const { _authToken } = get();
        if (!_authToken) return false;

        try {
          const response = await resilientFetch(
            `${SERVER_URL}/api/agents/${encodeURIComponent(slug)}`,
            {
              method: "PUT",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${_authToken}`,
              },
              body: JSON.stringify(input),
            }
          );

          if (!response.ok) {
            const data = await response.json().catch(() => ({}));
            throw new Error(
              data.error || `Failed to update agent: ${response.statusText}`
            );
          }

          await get().fetchMyAgents();
          return true;
        } catch (err) {
          set({
            error:
              err instanceof Error ? err.message : "Failed to update agent",
          });
          return false;
        }
      },

      deleteAgent: async (slug: string) => {
        const { _authToken } = get();
        if (!_authToken) return false;

        try {
          const response = await resilientFetch(
            `${SERVER_URL}/api/agents/${encodeURIComponent(slug)}`,
            {
              method: "DELETE",
              headers: {
                Authorization: `Bearer ${_authToken}`,
              },
            }
          );

          if (!response.ok) {
            const data = await response.json().catch(() => ({}));
            throw new Error(
              data.error || `Failed to delete agent: ${response.statusText}`
            );
          }

          await get().fetchMyAgents();
          return true;
        } catch (err) {
          set({
            error:
              err instanceof Error ? err.message : "Failed to delete agent",
          });
          return false;
        }
      },

      getAgentDetail: async (slug: string) => {
        const { _authToken } = get();
        if (!_authToken) return null;

        try {
          const response = await resilientFetch(
            `${SERVER_URL}/api/agents/${encodeURIComponent(slug)}`,
            {
              headers: {
                Authorization: `Bearer ${_authToken}`,
              },
            }
          );

          if (!response.ok) return null;
          const data = await response.json();
          return data.agent || null;
        } catch {
          return null;
        }
      },
    }),
    { name: "agent-store" }
  )
);
